/*
 * PRODUCT OPTION TOOLTIP
 *
 * Open on hover or click on desktop, tap on mobile, and close on outside tap or Escape.
 *
 * Every listener is delegated from `document` rather than bound to the trigger. That is
 * deliberate: <variant-picker> re-renders its own markup when the shopper changes variant,
 * which would throw away any listener attached directly to a button. Delegation survives
 * that, so the tooltip keeps working after a variant switch.
 *
 * The guard below matters because the snippet can render twice on one page — main product
 * plus a quick-view drawer — which would otherwise register every handler twice.
 */
(function () {
  if (window.__impuriaOptionTooltipReady) return;
  window.__impuriaOptionTooltipReady = true;

  var WRAPPER = '[data-option-tooltip]';
  var TRIGGER = '[data-option-tooltip-trigger]';
  var PANEL = '[data-option-tooltip-panel]';
  var EDGE_PADDING = 12;

  var openWrapper = null;

  function closest(node, selector) {
    return node && node.closest ? node.closest(selector) : null;
  }

  function panelOf(wrapper) {
    return wrapper.querySelector(PANEL);
  }

  /*
   * Nudge the panel back inside the viewport, then flip it above the icon if it would run
   * off the bottom. Measured after each change rather than calculated up front, because the
   * panel's width depends on how much text the merchant put in it.
   */
  function position(wrapper) {
    var panel = panelOf(wrapper);
    if (!panel) return;

    panel.style.left = '0px';
    panel.style.top = '';
    panel.style.bottom = '';

    var viewportWidth = document.documentElement.clientWidth;
    var rect = panel.getBoundingClientRect();
    var shift = 0;

    if (rect.right > viewportWidth - EDGE_PADDING) {
      shift = viewportWidth - EDGE_PADDING - rect.right;
    }

    if (rect.left + shift < EDGE_PADDING) {
      shift = EDGE_PADDING - rect.left;
    }

    if (shift !== 0) {
      panel.style.left = shift + 'px';
    }

    var viewportHeight = document.documentElement.clientHeight;
    var placed = panel.getBoundingClientRect();

    if (placed.bottom > viewportHeight - EDGE_PADDING) {
      var anchor = wrapper.getBoundingClientRect();

      // Only flip when there is genuinely more room above, otherwise it just moves the problem.
      if (anchor.top - placed.height - 8 > EDGE_PADDING) {
        panel.style.top = 'auto';
        panel.style.bottom = 'calc(100% + 8px)';
      }
    }
  }

  function open(wrapper) {
    if (openWrapper && openWrapper !== wrapper) close(openWrapper);

    var panel = panelOf(wrapper);
    var trigger = wrapper.querySelector(TRIGGER);
    if (!panel) return;

    panel.hidden = false;
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    openWrapper = wrapper;
    position(wrapper);
  }

  function close(wrapper) {
    var panel = panelOf(wrapper);
    var trigger = wrapper.querySelector(TRIGGER);

    if (panel) panel.hidden = true;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (openWrapper === wrapper) openWrapper = null;
  }

  document.addEventListener('click', function (event) {
    var trigger = closest(event.target, TRIGGER);

    if (trigger) {
      /*
       * preventDefault is required, not cosmetic: in dropdown selector mode the trigger sits
       * inside the option's <label>, so without it a tap would also activate the <select>.
       */
      event.preventDefault();
      event.stopPropagation();

      var wrapper = closest(trigger, WRAPPER);
      if (!wrapper) return;

      var panel = panelOf(wrapper);
      if (panel && panel.hidden) {
        open(wrapper);
      } else {
        close(wrapper);
      }
      return;
    }

    if (openWrapper && !closest(event.target, WRAPPER)) {
      close(openWrapper);
    }
  });

  // Hover is added only where a real pointer exists, so a tap on a touch screen cannot both
  // open via hover emulation and immediately toggle shut via the click above.
  if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
    document.addEventListener('mouseover', function (event) {
      var wrapper = closest(event.target, WRAPPER);
      if (wrapper) open(wrapper);
    });

    document.addEventListener('mouseout', function (event) {
      var wrapper = closest(event.target, WRAPPER);
      if (wrapper && !wrapper.contains(event.relatedTarget)) close(wrapper);
    });
  }

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && openWrapper) close(openWrapper);
  });

  window.addEventListener('resize', function () {
    if (openWrapper) position(openWrapper);
  });

  // Capture phase, so scrolling inside the product column repositions it too, not just the page.
  window.addEventListener('scroll', function () {
    if (openWrapper) position(openWrapper);
  }, true);
})();
